import React from 'react';
import {
  Box,
  Button,
  Stack,
  TextField,
  Typography,
  CircularProgress,
  Paper,
} from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import TableViewIcon from '@mui/icons-material/TableView';
import RefreshIcon from '@mui/icons-material/Refresh';
import { defaultReportRange, exportReportPdf, printElementById } from './reportExport';

type Props = {
  title: string;
  printRootId: string;
  loading?: boolean;
  dateFrom: string;
  dateTo: string;
  onDateFrom: (v: string) => void;
  onDateTo: (v: string) => void;
  onRefresh: () => void;
  onCsv?: () => void;
  children: React.ReactNode;
  extraFilters?: React.ReactNode;
};

export function useReportDates(days = 30) {
  const init = defaultReportRange(days);
  const [dateFrom, setDateFrom] = React.useState(init.date_from);
  const [dateTo, setDateTo] = React.useState(init.date_to);
  return { dateFrom, dateTo, setDateFrom, setDateTo };
}

const ReportShell: React.FC<Props> = ({
  title,
  printRootId,
  loading,
  dateFrom,
  dateTo,
  onDateFrom,
  onDateTo,
  onRefresh,
  onCsv,
  children,
  extraFilters,
}) => {
  return (
    <Box>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1.5}
        alignItems={{ sm: 'center' }}
        justifyContent="space-between"
        sx={{ mb: 2 }}
        className="no-print"
      >
        <Typography variant="h5" fontWeight={700}>
          {title}
        </Typography>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <TextField
            size="small"
            type="date"
            label="시작"
            InputLabelProps={{ shrink: true }}
            value={dateFrom}
            onChange={(e) => onDateFrom(e.target.value)}
          />
          <TextField
            size="small"
            type="date"
            label="종료"
            InputLabelProps={{ shrink: true }}
            value={dateTo}
            onChange={(e) => onDateTo(e.target.value)}
          />
          {extraFilters}
          <Button startIcon={<RefreshIcon />} variant="outlined" onClick={onRefresh}>
            조회
          </Button>
          <Button
            startIcon={<PrintIcon />}
            variant="outlined"
            onClick={() => printElementById(printRootId)}
          >
            인쇄
          </Button>
          <Button
            startIcon={<PictureAsPdfIcon />}
            variant="contained"
            onClick={() => exportReportPdf(printRootId, title)}
          >
            PDF
          </Button>
          {onCsv && (
            <Button startIcon={<TableViewIcon />} variant="outlined" onClick={onCsv}>
              CSV
            </Button>
          )}
        </Stack>
      </Stack>
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Paper id={printRootId} elevation={0} sx={{ p: { xs: 1, md: 2 }, bgcolor: 'background.paper' }}>
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
            기간: {dateFrom} ~ {dateTo}
          </Typography>
          {children}
        </Paper>
      )}
    </Box>
  );
};

export default ReportShell;
